import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  subscribeToOrgAppointments,
  type AppointmentRealtimeRow,
} from '@pc/db';
import { useToast } from '@pc/ui';

export interface CalendarPublicActionWatcherProps {
  /**
   * Active org. When this changes (e.g. workspace switch) we tear down
   * the previous channel and open a new one filtered to the new org.
   * `null` = disabled (e.g. no org selected yet).
   */
  orgId: string | null;
}

/**
 * Subscribes to `calendar.appointments` UPDATE events scoped to the
 * active org. Surfaces a toast (with click-to-open navigation) when a
 * status transition was triggered by the public /c/<token> link — so
 * the operator sees "Jordan just confirmed the 3 PM call" live instead
 * of refreshing the agenda.
 *
 * Toast triggers only on `confirmation_source === 'public_link'`
 * (transition to confirmed) or `cancellation_source === 'public_link'`
 * (transition to cancelled). Operator-initiated flips don't toast
 * (they already see the agenda update via their own mutation's query
 * invalidation).
 *
 * Implementation notes:
 *
 *   * We dedupe by `(appointmentId, source, status)` because Postgres
 *     replicates one ROW image per UPDATE — re-rendering after an
 *     unrelated field flip (e.g. the operator editing the title later)
 *     should NOT re-fire the "they just confirmed" toast.
 *   * Mounted at the AppShell level (sibling of
 *     `ShopMessageNotificationWatcher`), returns null.
 */
export function CalendarPublicActionWatcher({ orgId }: CalendarPublicActionWatcherProps) {
  const toast = useToast();
  const navigate = useNavigate();
  const notifiedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!orgId) return;

    const sub = subscribeToOrgAppointments(orgId, (next: AppointmentRealtimeRow) => {
      const isPublicConfirm =
        next.status === 'confirmed' && next.confirmation_source === 'public_link';
      const isPublicCancel =
        next.status === 'cancelled' && next.cancellation_source === 'public_link';

      if (!isPublicConfirm && !isPublicCancel) return;

      const dedupeKey = `${next.id}:${isPublicConfirm ? 'confirm' : 'cancel'}`;
      if (notifiedRef.current.has(dedupeKey)) return;
      notifiedRef.current.add(dedupeKey);

      const message = isPublicConfirm
        ? `Confirmed: ${next.title}`
        : `Cancelled: ${next.title}`;
      const tone: 'success' | 'warn' = isPublicConfirm ? 'success' : 'warn';

      toast.push(message, tone, {
        onClick: () => navigate(`/calendar/${next.id}`),
      });
    });

    return () => sub.unsubscribe();
  }, [orgId, toast, navigate]);

  return null;
}
